// Angular imports
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { SafeUrl } from '@angular/platform-browser';

// RxJs imports
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// Service imports
import { HelperService } from '../../_services/shared-services/helper.service';
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// Model imports
import { UserBiographyParametersDto } from '../_models/user-biography-parameters-dto';
import { UserBiographyResultDto } from '../_models/user-biography-result-dto';
import { AchievementBadgeResultDto } from '../_models/achievement-badge-result-dto';
import { PortfolioAssignmentsResultDto } from '../_models/portfolio-assignments-result-dto';
import { UserParametersDto } from '../_models/user-parameters-dto';

// Constant imports
import { URLConstants } from '../../_constants/url-constants';
import { CacheDuration } from '../../_constants/cache-duration';

@Injectable({
    providedIn: 'root'
})
export class UserBiographyService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly helperService: HelperService,
        private readonly cachingService: MPCacheRegistrationService
    ) {}

    getUserBiography(params: UserBiographyParametersDto): Observable<UserBiographyResultDto> {
        const url = `${URLConstants.ccUserBaseApiUrl}/bio${params.getQueryString()}`;
        this.cachingService.registerForCache(url, params.clearCache);
        const headers = this.cachingService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);

        return this.httpClient
            .get(url, { headers })
            .pipe(map((response: any) => plainToClass(UserBiographyResultDto, response)));
    }

    getUserAchivementBadge(achivement: AchievementBadgeResultDto): Observable<SafeUrl | string> {
        return this.helperService.getImageSafeUrlFromUrl(achivement.badgeImageUrl).pipe(
            catchError(() => {
                return of(achivement.unknownBadgeImage);
            })
        );
    }

    getUserPortfolioAssignment(userId: string, clearCache: boolean): Observable<PortfolioAssignmentsResultDto[]> {
        const url = `${URLConstants.ccUserBaseApiUrl}/portfolioassignments?userId=${userId}&clearCache=${clearCache}`;
        this.cachingService.registerForCache(url, clearCache);
        const headers = this.cachingService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);

        return this.httpClient
            .get(url, { headers })
            .pipe(
                map((response: PortfolioAssignmentsResultDto[]) =>
                    plainToClass(PortfolioAssignmentsResultDto, response)
                )
            );
    }

    deleteUserProfile(params: UserParametersDto): Observable<any> {
        const url = `${URLConstants.ccUserBaseApiUrl}/bio/delete`;
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: classToPlain(params)
        };
        return this.httpClient.delete(url, options);
    }

    approveUserProfile(params: UserParametersDto): Observable<any> {
        const url = `${URLConstants.ccUserBaseApiUrl}/bio/approve`;
        return this.httpClient.post(url, classToPlain(params));
    }
}
